import graphql from "babel-plugin-relay/macro";
import { useMutation } from "react-relay";

import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { useCallback, useState } from "react";
import {
  useImageUploaderMutation,
  useImageUploaderMutation$data,
} from "./__generated__/useImageUploaderMutation.graphql";

export interface Image {
  key: string;
  name: string;
  timestamp: number;
  path?: string;
  downloadUrl?: string;
  thumbnailUrl?: string;
  metadata?: Metadata;
}

interface Metadata {
  step?: string;
  description?: string;
  optional?: string;
}

interface Uploader {
  images: Image[];
  addImages: (images: Image[], clear?: boolean) => void;
  addImageList: (images?: FileList, clear?: boolean, metadata?: Object) => void;
  deleteImage: (key: string) => void;
  uploadImages: () => Promise<Image[] | undefined>;
  resetImages: () => void;
  uploading: boolean;
  setImageDescription: (
    images: Image[],
    key: string,
    description: string
  ) => void;
}

export function useImageUploader(ixs: Image[]): Uploader {
  const [images, setImages] = useState<Image[]>(ixs);
  const [uploading, setUploading] = useState<boolean>(false);
  const [getUploadURL] = useMutation<useImageUploaderMutation>(
    graphql`
      mutation useImageUploaderMutation(
        $timestamp: Float!
        $filetype: String!
        $type: String!
        $filesize: Float!
        $filename: String
        $metadata: JSONString
      ) {
        getUploadUrl(
          input: {
            timestamp: $timestamp
            filetype: $filetype
            type: $type
            filesize: $filesize
            filename: $filename
            metadata: $metadata
          }
        ) {
          presignedUrl
          storageObject {
            id
            key
            filename
            createdOn
            downloadUrl
            thumbnailUrl
            metaData
          }
        }
      }
    `
  );
  const setImageDescription = useCallback(
    (images: Image[], imageKey: string, description: string) => {
      const image = images.find(({ key }) => key === imageKey);
      if (!image) return images;
      setImages((images) => {
        return [
          ...images.filter(({ key }) => key !== imageKey),
          {
            ...image,
            key: imageKey,
            metadata: {
              ...(image.metadata || {}),
              description,
            },
          },
        ];
      });
    },
    []
  );
  const addImages = useCallback((newImages: Image[]) => {
    setImages((images) => [
      ...images?.filter(({ key }) => !newImages?.find((i) => i.key === key)),
      ...newImages,
    ]);
  }, []);
  const addImageList = useCallback(
    (images?: FileList, clear?: boolean, metadata?: Metadata) => {
      images &&
        (clear ? setImages : addImages)(
          Object.values(images)?.map((image) => ({
            path: URL.createObjectURL(image),
            key: uuidv4(),
            name: image.name,
            timestamp: new Date().getTime() / 1000,
            ...(metadata ? { metadata } : {}),
          })) || []
        );
    },
    [addImages]
  );
  const resetImages = useCallback(() => {
    setImages([]);
  }, []);
  const deleteImage = useCallback((key?: string) => {
    setImages((oldImages) => [
      ...oldImages?.filter((image) =>
        key !== undefined ? image.key !== key : !!image.downloadUrl
      ),
    ]);
  }, []);
  const uploadImages = useCallback(async () => {
    const uploadTargets = images.filter(
      (image) => !image.downloadUrl && image.path
    );
    const uploadPromises: Promise<
      [useImageUploaderMutation$data["getUploadUrl"], string]
    >[] = uploadTargets.map(async (image) => {
      const file = await fetch(image.path!).then((r) => r.blob());
      return new Promise((resolve, reject) => {
        getUploadURL({
          variables: {
            timestamp: image.timestamp,
            filename: image.name,
            filetype: file.type,
            type: "i",
            filesize: file.size,
            ...(image.metadata?.step
              ? {
                  metadata: JSON.stringify({
                    step: image.metadata?.step || "",
                    filename: image.name || "",
                    timestamp: (image.timestamp || "").toString(),
                  }),
                }
              : {}),
          },
          onCompleted(response) {
            if (
              !response.getUploadUrl?.presignedUrl ||
              !response.getUploadUrl?.storageObject?.key
            ) {
              reject("Upload URL or Storage Object Key missing");
              return;
            }
            axios(response.getUploadUrl?.presignedUrl, {
              method: "PUT",
              data: file,
              headers: {
                "Content-Type": file.type,
                "Access-Control-Allow-Origin": "*",
                ...(image.metadata?.step
                  ? {
                      "x-amz-meta-filename": image.name || "",
                      "x-amz-meta-timestamp": (
                        image.timestamp || ""
                      ).toString(),
                      "x-amz-meta-step": image.metadata?.step || "",
                    }
                  : {}),
              },
              onUploadProgress: (p) => {
                if (p.loaded === p.total) {
                  URL.revokeObjectURL(image.path!);
                  resolve([response.getUploadUrl, image.key]);
                }
              },
            }).catch(reject);
          },
        });
      });
    });
    try {
      setUploading(true);
      let newlyUploaded = await Promise.all(uploadPromises);
      let newImages = images?.map((image) => {
        let newImage = newlyUploaded?.find(([_, oK]) => image.key === oK);
        if (newImage?.length) {
          let returnObject = newImage[0]?.storageObject;
          return {
            ...image,
            key: String(returnObject?.key),
            downloadUrl: String(returnObject?.downloadUrl),
          };
        }
        return image;
      });
      setUploading(false);
      setImages(newImages);
      return newImages;
    } catch (error) {
      console.error("Failed to upload all images: ", error);
    }
  }, [images, getUploadURL]);
  return {
    images,
    addImages,
    addImageList,
    deleteImage,
    uploadImages,
    resetImages,
    uploading,
    setImageDescription,
  };
}
